import type { FC, PropsWithChildren } from "react";
import { Button, Card } from "@ptsd/uikit";
import type { ViewProps } from "tamagui";
import { Anchor, Text, View, YStack } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { api } from "~/utils/api";
import { amountToHuman } from "~/utils/formatters";
import config from "~/config";
import { IconBarchart } from "./icons/icon-barchart";
import { IconChartUp } from "./icons/icon-chart-up";
import { IconCheckmark } from "./icons/icon-checkmark";
import { IconHeart } from "./icons/icon-heart";
import { IconRepeat } from "./icons/icon-repeat";
import { IconStars } from "./icons/icon-stars";

type PaymentPlan = RouterOutputs["payments"]["getPlans"]["plans"][number];

export const PaymentCard: FC<{ isPaywall?: boolean }> = ({
  isPaywall = false,
}) => {
  const [{ plans }] = api.payments.getPlans.useSuspenseQuery();
  if (!isPaywall) return null;

  const plan = plans.at(0);
  if (!plan) return null;

  return (
    <Card
      shadow={"none"}
      variant={"borderless"}
      size={"sm"}
      $gtMd={{
        size: "md",
        borderColor: "$gray-200",
        borderWidth: "$0.25",
        paddingVertical: 0,
      }}
    >
      <Line />
      <YStack marginVertical={"$5"} gap={"$4"}>
        <YStack gap={"$2"}>
          <Card.Title
            $gtMd={{ fontSize: 54, lineHeight: "$9", fontWeight: "$7" }}
            style={{
              background:
                "linear-gradient(90deg, #10B981 0%, #3B82F6 33%, #8B5CF6 66%, #EC4899 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Зрозумійте свій стан тут і зараз
          </Card.Title>
          <Card.Subtitle textAlign="center">
            Відкрийте доступ до розширених результатів: ви побачите повну
            картину свого стану, зможете зрозуміти, які сфери потребують уваги.
          </Card.Subtitle>
        </YStack>
        <Price plan={plan} />
        <Benefits>
          <BenefitsItem
            Icon={IconChartUp}
            borderColor={"#E4EBFF"}
            backgroundColor={"#F8F7F9"}
          >
            Оцінку вашого стану (до 55 показників)
          </BenefitsItem>
          <BenefitsItem
            Icon={IconCheckmark}
            borderColor={"#CFFFEE"}
            backgroundColor={"#FAFFFA"}
          >
            Зрозумілі пояснення показників
          </BenefitsItem>
          <BenefitsItem
            Icon={IconStars}
            borderColor={"#EAE1FD"}
            backgroundColor={"#FAF9FE"}
          >
            Персоналізовані рекомендації
          </BenefitsItem>
          <BenefitsItem
            Icon={IconHeart}
            borderColor={"#FFDCED"}
            backgroundColor={"#FFF4FA"}
          >
            Завантаження результатів
          </BenefitsItem>
          <BenefitsItem
            Icon={IconRepeat}
            borderColor={"#CAF7FF"}
            backgroundColor={"#F4FDFF"}
          >
            Повторний чекап
          </BenefitsItem>
          <BenefitsItem
            Icon={IconBarchart}
            borderColor={"#FFF2DB"}
            backgroundColor={"#FDFAF5"}
          >
            Динаміка змін показників
          </BenefitsItem>
        </Benefits>
        <ResultsAccess plan={plan} />
      </YStack>
      <Line $gtMd={{ display: "none" }} />
    </Card>
  );
};

const Line: FC<ViewProps> = (props) => {
  return (
    <View
      marginVertical={-2}
      height={12}
      $sm={{ marginHorizontal: -24 }}
      style={{
        background:
          "linear-gradient(90deg, #10B981 0%, #3B82F6 33%, #8B5CF6 66%, #EC4899 100%)",
      }}
      {...props}
    />
  );
};

const ResultsAccess: FC<{ plan: PaymentPlan }> = ({ plan }) => {
  return (
    <Card
      variant="gray"
      backgroundColor={"#F8F7F9"}
      alignSelf="center"
      paddingVertical={"$4"}
      $sm={{ marginHorizontal: -20, paddingHorizontal: 20, borderRadius: 0 }}
      $gtMd={{ paddingHorizontal: "$12", maxWidth: 660 }}
    >
      <YStack gap={"$2"} alignItems="center">
        <Anchor
          href={new URL("payment/link/" + plan.id, config.API_URL).toString()}
          textDecorationLine="none"
          asChild
        >
          <Button
            rounded={"lg"}
            sizes="lg"
            $gtMd={{ width: 440 }}
            style={{
              background: "linear-gradient(137deg, #064E3B 0%, #065F46 87.83%)",
            }}
          >
            <Text
              fontFamily={"$body"}
              textAlign="center"
              fontSize={"$5"}
              lineHeight={"$5"}
              fontWeight={600}
              color={"white"}
            >
              Отримати повні результати
            </Text>
          </Button>
        </Anchor>
        <Text
          fontFamily={"$body"}
          textAlign="center"
          fontSize={"$4"}
          lineHeight={"$4"}
          fontWeight={500}
        >
          Я хочу отримати повні результати та почати піклуватись про себе
        </Text>
      </YStack>
    </Card>
  );
};

export const Price: FC<{ plan: PaymentPlan }> = ({ plan }) => {
  return (
    <View
      alignItems="center"
      justifyContent="center"
      gap={"$3"}
      $gtMd={{ flexDirection: "row" }}
    >
      {plan.amountBeforeSale !== null && (
        <Text
          fontSize={"$5"}
          fontFamily={"$body"}
          textAlign="center"
          $gtSm={{ fontSize: "$6" }}
          fontWeight={"400"}
          color={"$gray-200"}
          textDecorationLine={"line-through"}
        >
          {amountToHuman(plan.amountBeforeSale)}
        </Text>
      )}
      <Card
        borderRadius={"$5"}
        paddingHorizontal={"$3"}
        paddingVertical={"$1.3"}
        borderWidth={"$0.1"}
        borderStyle="solid"
        borderColor={"#90FAC7"}
        style={{
          background: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)",
          boxShadow: "0 4px 20px 0 rgba(16, 185, 129, 0.15)",
        }}
      >
        <Text
          fontSize={"$5"}
          fontFamily={"$body"}
          textAlign="center"
          $gtMd={{ fontSize: "$7" }}
          style={{ color: "#047857", fontWeight: "700" }}
        >
          {amountToHuman(plan.amount)}
        </Text>
      </Card>
    </View>
  );
};

export const Benefits: FC<ViewProps> = (props) => {
  return (
    <View
      width={"100%"}
      flexDirection={"column"}
      alignSelf="center"
      gap={"$2"}
      marginBottom={"$2.5"}
      $gtMd={{ maxWidth: 660 }}
      {...props}
    />
  );
};

const BenefitsItem: FC<PropsWithChildren<{ Icon: FC }> & ViewProps> = ({
  Icon,
  children,
  ...props
}) => {
  return (
    <Card
      $sm={{ gap: "$1" }}
      gap={"$2"}
      flexDirection={"row"}
      alignItems={"center"}
      paddingHorizontal={"$1.3"}
      paddingVertical={"$1.5"}
      borderRadius={"$5"}
      {...props}
    >
      <Icon />
      <Text
        fontSize={14}
        fontWeight={500}
        fontFamily={"$body"}
        marginLeft={"$0.5"}
        $gtMd={{ fontSize: 16 }}
      >
        {children}
      </Text>
    </Card>
  );
};
